import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { Router, ActivatedRoute } from '@angular/router';
import { UserService, User, Client, Module } from '../../../services/user.service';
import { ControlDusService } from '../../../services/control-dus.service';
import { AuthService } from '../../../services/auth.service';
import { catchError, finalize, map, startWith } from 'rxjs/operators';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TransportistaOption } from '@opb-workspace/shared';

@Component({
    selector: 'app-edit-user-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatCardModule,
        MatAutocompleteModule,
    ],
    templateUrl: './edit-user-page.component.html',
    styleUrl: './edit-user-page.component.scss'
})
export class EditUserPageComponent implements OnInit {
    userForm: FormGroup;
    isLoading = false;
    isLoadingClients = false;
    isLoadingUser = false;
    isPasswordChange = false;
    userId: string | null = null;
    user: User | null = null;

    roles = [
        { value: 'admin', label: 'Administrador' },
        { value: 'operator', label: 'Operador' },
        { value: 'client', label: 'Cliente' },
        { value: 'consignatario', label: 'Consignatario' },
        { value: 'transportista', label: 'Transportista' }
    ];

    clients: Client[] = [];
    clientControl = new FormControl<string | Client>('');
    filteredClients!: Observable<Client[]>;

    consignees: { consigneeId: number; consigneeShortName: string }[] = [];
    isLoadingConsignees = false;

    // feat-rol-transportista: la lista se pide solo con el rol transportista (consulta SQL Server)
    transportistas: TransportistaOption[] = [];
    isLoadingTransportistas = false;
    transportistasLoadError: string | null = null;
    private transportistasLoaded = false;

    availableModules: Module[] = [];
    selectedModuleCodes: string[] = [];
    isLoadingModules = false;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private fb: FormBuilder,
        // eslint-disable-next-line no-unused-vars
        private userService: UserService,
        // eslint-disable-next-line no-unused-vars
        private controlDusService: ControlDusService,
        // eslint-disable-next-line no-unused-vars
        private authService: AuthService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {
        this.userForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            phone: ['', [Validators.required]],
            role: ['operator', [Validators.required]],
            clientId: [null],
            consigneeId: [null],
            transportistaId: [null],
            password: [''],
            confirmPassword: [{ value: '', disabled: true }],
            isActive: [true]
        }, { validators: this.passwordMatchValidator });

        // Escuchar cambios en el rol para validar cliente/consignatario
        this.userForm.get('role')?.valueChanges.subscribe(role => {
            const clientIdControl = this.userForm.get('clientId');
            const consigneeIdControl = this.userForm.get('consigneeId');
            this.applyTransportistaRole(role);
            if (role === 'client') {
                clientIdControl?.setValidators([Validators.required]);
                this.clientControl.setValidators([Validators.required]);
                consigneeIdControl?.clearValidators();
                consigneeIdControl?.setValue(null);
            } else if (role === 'consignatario') {
                consigneeIdControl?.setValidators([Validators.required]);
                clientIdControl?.clearValidators();
                clientIdControl?.setValue(null);
                this.clientControl.clearValidators();
                this.clientControl.setValue('');
            } else {
                clientIdControl?.clearValidators();
                clientIdControl?.setValue(null);
                this.clientControl.clearValidators();
                this.clientControl.setValue('');
                consigneeIdControl?.clearValidators();
                consigneeIdControl?.setValue(null);
            }
            clientIdControl?.updateValueAndValidity();
            this.clientControl.updateValueAndValidity();
            consigneeIdControl?.updateValueAndValidity();
        });

        // Escuchar cambios en la contraseña para validar
        this.userForm.get('password')?.valueChanges.subscribe(password => {
            this.isPasswordChange = password && password.length > 0;
            const confirmPasswordControl = this.userForm.get('confirmPassword');
            
            if (this.isPasswordChange) {
                confirmPasswordControl?.enable();
                confirmPasswordControl?.setValidators([Validators.required]);
                this.userForm.get('password')?.setValidators([
                    Validators.required, 
                    Validators.minLength(8), 
                    Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]/)
                ]);
            } else {
                confirmPasswordControl?.disable();
                confirmPasswordControl?.clearValidators();
                confirmPasswordControl?.setValue('');
                this.userForm.get('password')?.clearValidators();
                this.userForm.get('password')?.setValidators([]);
            }
            
            confirmPasswordControl?.updateValueAndValidity();
            this.userForm.get('password')?.updateValueAndValidity();
        });
    }

    ngOnInit(): void {
        this.userId = this.route.snapshot.paramMap.get('id');
        if (this.userId) {
            this.loadInitialData();
        } else {
            this.snackBar.open('ID de usuario no válido', 'Cerrar', {
                duration: 5000,
                panelClass: ['error-snackbar']
            });
            // Preservar query params al regresar al listado
            const queryParams = this.route.snapshot.queryParams;
            this.router.navigate(['/users'], { queryParams });
        }
    }

    private loadInitialData(): void {
        if (!this.userId) return;

        this.isLoadingUser = true;
        this.isLoadingConsignees = true;
        this.controlDusService.getConsignees()
            .pipe(catchError(() => of([])), finalize(() => { this.isLoadingConsignees = false; }))
            .subscribe(data => { this.consignees = data; });

        forkJoin({
            userResponse: this.userService.getUserById(this.userId),
            clientsResponse: this.userService.getActiveClients(),
            modulesResponse: this.userService.getAvailableModules()
        }).pipe(
            catchError(error => {
                this.logger.error('Error al cargar datos iniciales', error as Error, 'EditUserPageComponent');
                this.snackBar.open('Error al cargar la información del usuario o clientes', 'Cerrar', {
                    duration: 5000,
                    panelClass: ['error-snackbar']
                });
                // Preservar query params al regresar al listado
                const queryParams = this.route.snapshot.queryParams;
                this.router.navigate(['/users'], { queryParams });
                return throwError(() => error);
            }),
            finalize(() => {
                this.isLoadingUser = false;
                this.isLoadingClients = false;
            })
        ).subscribe(({ userResponse, clientsResponse, modulesResponse }) => {
            // Manejar clientes
            if (clientsResponse.success && clientsResponse.data) {
                this.clients = clientsResponse.data;
            } else {
                this.logger.warn('No se pudieron cargar los clientes', 'EditUserPageComponent');
            }

            // Manejar módulos
            if (modulesResponse.success && modulesResponse.data) {
                // Filtrar solo módulos activos
                // El backend ya filtra por módulos activos, pero por seguridad también filtramos aquí
                // El backend ahora devuelve isActive (camelCase)
                this.availableModules = modulesResponse.data.filter(module => {
                    const isActive = (module as any).isActive !== undefined 
                        ? (module as any).isActive 
                        : (module as any).is_active !== undefined 
                        ? (module as any).is_active 
                        : true;
                    return isActive === true;
                });
                this.logger.debug(`Módulos filtrados: ${this.availableModules.length} activos de ${modulesResponse.data.length} totales`, 'EditUserPageComponent');
            } else {
                this.logger.warn('No se pudieron cargar los módulos', 'EditUserPageComponent');
            }

            // Manejar usuario
            if (userResponse.success && userResponse.data) {
                this.user = userResponse.data;
                this.populateForm(); // Ahora es seguro llamar a esto
                this.loadUserModules(); // Cargar módulos del usuario
            } else {
                this.snackBar.open('Usuario no encontrado', 'Cerrar', {
                    duration: 5000,
                    panelClass: ['error-snackbar']
                });
                // Preservar query params al regresar al listado
                const queryParams = this.route.snapshot.queryParams;
                this.router.navigate(['/users'], { queryParams });
            }
        });

        this.filteredClients = this.clientControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterClients(value)),
        );
    }

    private populateForm(): void {
        if (!this.user) {
            this.logger.error('Se intentó poblar el formulario sin datos de usuario.', new Error('Usuario nulo'), 'EditUserPageComponent');
            return;
        }

        this.userForm.patchValue({
            email: this.user.email,
            firstName: this.user.first_name,
            lastName: this.user.last_name,
            phone: this.user.phone,
            role: this.user.role,
            clientId: this.user.client_id || null,
            consigneeId: this.user.consignee_id || null,
            transportistaId: this.user.transportista_id ?? null,
            isActive: this.user.is_active
        });

        const userClientId = this.user?.client_id ?? null;
        if (userClientId) {
            const client = this.clients.find(c => c.clienteId === userClientId);
            if (client) {
                this.clientControl.setValue(client);
            }
        }
    }

    private loadUserModules(): void {
        if (!this.userId) return;

        this.userService.getUserModules(this.userId)
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar módulos del usuario', error as Error, 'EditUserPageComponent');
                    return of({ success: false, data: [] });
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    this.selectedModuleCodes = response.data.map(module => module.code);
                }
            });
    }

    onModuleToggle(moduleCode: string, event: any): void {
        if (event.checked) {
            if (!this.selectedModuleCodes.includes(moduleCode)) {
                this.selectedModuleCodes.push(moduleCode);
            }
        } else {
            this.selectedModuleCodes = this.selectedModuleCodes.filter(code => code !== moduleCode);
        }
    }

    isModuleSelected(moduleCode: string): boolean {
        return this.selectedModuleCodes.includes(moduleCode);
    }

    private loadClients(): void {
        this.isLoadingClients = true;
        this.userService.getActiveClients()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar clientes', error as Error, 'EditUserPageComponent');
                    this.snackBar.open('Error al cargar la lista de clientes', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoadingClients = false;
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    this.clients = response.data;
                    // Si el usuario ya se cargó, intentar popular el campo de cliente
                    if (this.user) {
                        this.populateForm();
                    }
                }
            });
    }

    shouldShowClientSelect(): boolean {
        return this.userForm.get('role')?.value === 'client';
    }

    shouldShowTransportistaSelect(): boolean {
        return this.userForm.get('role')?.value === 'transportista';
    }

    /** Requerido solo con rol transportista; al cambiar a otro rol se oculta y se limpia. */
    private applyTransportistaRole(role: string): void {
        const control = this.userForm.get('transportistaId');
        if (role === 'transportista') {
            control?.setValidators([Validators.required]);
            this.loadTransportistas();
        } else {
            control?.clearValidators();
            control?.setValue(null);
        }
        control?.updateValueAndValidity();
    }

    private loadTransportistas(): void {
        if (this.transportistasLoaded || this.isLoadingTransportistas) return;
        this.isLoadingTransportistas = true;
        this.transportistasLoadError = null;
        this.userService.getTransportistas()
            .pipe(finalize(() => { this.isLoadingTransportistas = false; }))
            .subscribe({
                next: data => {
                    this.transportistas = data;
                    this.transportistasLoaded = true;
                },
                error: error => {
                    this.logger.error('Error al cargar transportistas', error as Error, 'EditUserPageComponent');
                    this.transportistasLoadError =
                        'No se pudo cargar la lista de transportistas. Recargue la página para reintentar.';
                },
            });
    }

    shouldShowConsigneeSelect(): boolean {
        return this.userForm.get('role')?.value === 'consignatario';
    }

    private _filterClients(value: string | Client | null): Client[] {
        if (!value) {
            return this.clients.slice();
        }
        const filterValue = (typeof value === 'string' ? value : value.cliente).toLowerCase();
        return this.clients.filter(client => client.cliente.toLowerCase().includes(filterValue));
    }

    displayClient(client: Client): string {
        return client && client.cliente ? client.cliente : '';
    }

    onClientSelected(event: any): void {
        const selectedClient = event.option.value as Client;
        this.userForm.get('clientId')?.setValue(selectedClient.clienteId);
    }

    passwordMatchValidator(group: FormGroup): { [key: string]: any } | null {
        const password = group.get('password')?.value;
        const confirmPassword = group.get('confirmPassword')?.value;
        
        if (password && confirmPassword && password !== confirmPassword) {
            return { passwordMismatch: true };
        }
        return null;
    }

    getErrorMessage(fieldName: string): string {
        const control = this.userForm.get(fieldName);
        if (!control) return '';

        if (control.hasError('required')) {
            return 'Este campo es requerido';
        }

        if (control.hasError('email')) {
            return 'Ingrese un email válido';
        }

        if (control.hasError('minlength')) {
            const requiredLength = control.errors?.['minlength']?.requiredLength;
            return `Mínimo ${requiredLength} caracteres`;
        }

        if (control.hasError('pattern')) {
            return 'La contraseña debe contener al menos una mayúscula, un número y un carácter especial';
        }

        if (control.hasError('passwordMismatch')) {
            return 'Las contraseñas no coinciden';
        }

        return 'Campo inválido';
    }

    markFormGroupTouched(): void {
        Object.keys(this.userForm.controls).forEach(key => {
            const control = this.userForm.get(key);
            control?.markAsTouched();
        });
    }

    onSubmit(): void {
        // Sin la lista no se puede elegir ni validar el transportista (REQ-FE-4.5)
        if (this.shouldShowTransportistaSelect() && this.transportistasLoadError) {
            this.snackBar.open(this.transportistasLoadError, 'Cerrar', {
                duration: 5000,
                panelClass: ['error-snackbar']
            });
            return;
        }
        if (this.userForm.valid && this.userId) {
            this.isLoading = true;
            
            const formData = this.userForm.value;
            const userData: any = {
                email: formData.email,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone: formData.phone,
                role: formData.role,
                client_id: formData.clientId,
                consignee_id: formData.consigneeId,
                ...(formData.role === 'transportista' ? { transportista_id: formData.transportistaId } : {}),
                is_active: formData.isActive
            };

            // Solo incluir contraseña si se está cambiando
            if (this.isPasswordChange) {
                userData.password = formData.password;
            }

            // Primero actualizar el usuario
            this.userService.updateUser(this.userId, userData)
                .pipe(
                    catchError((error: any) => {
                        this.logger.error('Error al actualizar usuario', error as Error, 'EditUserPageComponent');
                        
                        let errorMessage = 'Error al actualizar el usuario';
                        
                        // Manejar diferentes tipos de errores
                        if (error.error?.message) {
                            errorMessage = error.error.message;
                        } else if (error.error?.error?.message) {
                            // Para errores de validación de class-validator
                            errorMessage = error.error.error.message;
                        } else if (error.error?.error) {
                            // Para errores de validación anidados
                            errorMessage = error.error.error;
                        } else if (error.message) {
                            errorMessage = error.message;
                        } else if (error.status === 400) {
                            // Para errores de validación, intentar extraer el mensaje específico
                            if (error.error?.message) {
                                errorMessage = error.error.message;
                            } else if (error.error?.error) {
                                errorMessage = error.error.error;
                            } else if (Array.isArray(error.error)) {
                                // Para errores de validación en array
                                errorMessage = error.error.map((e: any) => e.message || e).join(', ');
                            } else {
                                errorMessage = 'Los datos proporcionados no son válidos. Verifique la información ingresada.';
                            }
                        } else if (error.status === 409) {
                            errorMessage = 'El email ya está registrado por otro usuario.';
                        } else if (error.status === 404) {
                            errorMessage = 'El usuario no fue encontrado.';
                        } else if (error.status === 401) {
                            errorMessage = 'No tiene permisos para realizar esta acción.';
                        }
                        
                        this.snackBar.open(errorMessage, 'Cerrar', {
                            duration: 8000,
                            panelClass: ['error-snackbar']
                        });
                        return of(null);
                    }),
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe((response: any) => {
                    if (response && response.success) {
                        // Actualizar módulos del usuario
                        this.userService.assignModules(this.userId!, this.selectedModuleCodes)
                            .pipe(
                                catchError(error => {
                                    this.logger.error('Error al actualizar módulos', error as Error, 'EditUserPageComponent');
                                    this.snackBar.open('Usuario actualizado pero hubo un error al actualizar módulos', 'Cerrar', {
                                        duration: 5000,
                                        panelClass: ['warning-snackbar']
                                    });
                                    return of(null);
                                })
                            )
                            .subscribe(modulesResponse => {
                                const queryParams = this.route.snapshot.queryParams;
                                const showSuccessAndNavigate = (message: string) => {
                                    this.snackBar.open(message, 'Cerrar', {
                                        duration: 4000,
                                        panelClass: ['success-snackbar']
                                    });
                                    this.router.navigate(['/users'], { queryParams });
                                };

                                if (modulesResponse && modulesResponse.success) {
                                    const currentUser = this.authService.getCurrentUser();
                                    const isEditingSelf = currentUser && currentUser.id === this.userId;
                                    const hasSkipped = Array.isArray(modulesResponse.skippedModuleCodes) && modulesResponse.skippedModuleCodes.length > 0;
                                    const successMessage = hasSkipped && modulesResponse.message
                                        ? modulesResponse.message
                                        : (isEditingSelf
                                            ? 'Usuario y módulos actualizados. El menú se ha actualizado.'
                                            : 'Usuario y módulos actualizados. El usuario deberá iniciar sesión de nuevo para ver los cambios.');

                                    if (isEditingSelf) {
                                        // Refrescar token para que el menú y permisos se actualicen de inmediato
                                        this.authService.refreshToken()
                                            .pipe(
                                                catchError(refreshError => {
                                                    this.logger.error('Error al refrescar token', refreshError as Error, 'EditUserPageComponent');
                                                    this.snackBar.open('Usuario actualizado. Cierre sesión y vuelva a entrar para ver los nuevos módulos.', 'Cerrar', {
                                                        duration: 5000,
                                                        panelClass: ['warning-snackbar']
                                                    });
                                                    return of(null);
                                                })
                                            )
                                            .subscribe(() => {
                                                this.logger.info('Token refrescado exitosamente', 'EditUserPageComponent');
                                                showSuccessAndNavigate(hasSkipped ? modulesResponse.message! : 'Usuario y módulos actualizados. El menú se ha actualizado.');
                                            });
                                    } else {
                                        showSuccessAndNavigate(successMessage);
                                    }
                                } else {
                                    showSuccessAndNavigate('Usuario actualizado exitosamente');
                                }
                            });
                    }
                });
        } else {
            this.markFormGroupTouched();
            
            // Mostrar mensaje específico si hay errores de validación de contraseña
            const passwordControl = this.userForm.get('password');
            const confirmPasswordControl = this.userForm.get('confirmPassword');
            
            if (this.isPasswordChange) {
                if (passwordControl?.errors) {
                    let passwordError = 'Error en la contraseña: ';
                    if (passwordControl.errors['required']) {
                        passwordError += 'La contraseña es requerida.';
                    } else if (passwordControl.errors['minlength']) {
                        passwordError += 'La contraseña debe tener al menos 8 caracteres.';
                    } else if (passwordControl.errors['pattern']) {
                        passwordError += 'La contraseña debe incluir al menos una mayúscula, un número y un carácter especial (@$!%*?&#).';
                    }
                    
                    this.snackBar.open(passwordError, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                } else if (confirmPasswordControl?.errors) {
                    let confirmError = 'Error en la confirmación: ';
                    if (confirmPasswordControl.errors['required']) {
                        confirmError += 'Debe confirmar la contraseña.';
                    } else if (confirmPasswordControl.errors['passwordMismatch']) {
                        confirmError += 'Las contraseñas no coinciden.';
                    }
                    
                    this.snackBar.open(confirmError, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                }
            }
        }
    }

    onCancel(): void {
        // Preservar query params al regresar al listado
        const queryParams = this.route.snapshot.queryParams;
        this.router.navigate(['/users'], { queryParams });
    }

    // Métodos de validación de contraseña para indicadores visuales
    hasUpperCase(password: string | null | undefined): boolean {
        return !!password && /[A-Z]/.test(password);
    }

    hasNumber(password: string | null | undefined): boolean {
        return !!password && /\d/.test(password);
    }

    hasSpecialChar(password: string | null | undefined): boolean {
        return !!password && /[@$!%*?&#]/.test(password);
    }
} 